import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'sonner';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import { ExperienceThemeProvider, useExperienceTheme } from './context/ExperienceThemeContext';
import Login from './pages/Login';
import Signup from './pages/Signup';
import Admin from './pages/Admin';
import Home from './pages/Home';
import Communication from './pages/Communication';
import MissionUpdate from './pages/MissionUpdate';
import MissionStats from './pages/MissionStats';
import MaydayDisplay from './pages/MaydayDisplay';
import ThemeSelection from './pages/ThemeSelection';
import FloatingMaydayButton from './components/FloatingMaydayButton';
import './App.css';

const LoadingScreen = () => (
  <div className="min-h-screen bg-[#050508] flex items-center justify-center">
    <div className="w-12 h-12 border-2 border-[#00f3ff] border-t-transparent rounded-full animate-spin" />
  </div>
);

const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  return children;
};

const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  if (isAuthenticated) return <Navigate to="/dashboard" replace />;
  return children;
};

// Route that checks if onboarding is needed
const OnboardingCheck = ({ children }) => {
  const { isAuthenticated, loading: authLoading } = useAuth();
  const { needsOnboarding, loading: themeLoading } = useExperienceTheme();
  
  if (authLoading || themeLoading) return <LoadingScreen />;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (needsOnboarding) return <Navigate to="/choose-theme" replace />;
  return children;
};

// Route specifically for theme selection
const ThemeSelectionRoute = ({ children }) => {
  const { isAuthenticated, loading: authLoading } = useAuth();
  const { needsOnboarding, loading: themeLoading } = useExperienceTheme();
  
  if (authLoading || themeLoading) return <LoadingScreen />;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  // Allow access even if they already have a theme (for changing in settings)
  return children;
};

function AppRoutes() {
  return (
    <>
      <Routes>
        <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
        <Route path="/signup" element={<PublicRoute><Signup /></PublicRoute>} />
        <Route path="/choose-theme" element={<ThemeSelectionRoute><ThemeSelection /></ThemeSelectionRoute>} />
        <Route path="/admin" element={<ProtectedRoute><Admin /></ProtectedRoute>} />
        <Route path="/dashboard" element={<OnboardingCheck><Home /></OnboardingCheck>} />
        <Route path="/communication" element={<OnboardingCheck><Communication /></OnboardingCheck>} />
        <Route path="/mission-update" element={<OnboardingCheck><MissionUpdate /></OnboardingCheck>} />
        <Route path="/mission-stats" element={<OnboardingCheck><MissionStats /></OnboardingCheck>} />
        <Route path="/mayday" element={<OnboardingCheck><MaydayDisplay /></OnboardingCheck>} />
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
      <FloatingMaydayButton />
      <Toaster position="top-center" toastOptions={{ duration: 3000 }} />
    </>
  );
}

function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <AuthProvider>
          <ExperienceThemeProvider>
            <AppRoutes />
          </ExperienceThemeProvider>
        </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}

export default App;
